import { Client } from "@xmpp/client"
import { getAllItems } from "./PubSub"
import { Element } from "@xmpp/xml"
import { wizardJid } from "@/jidUtils"
import XmppPubSubApi from "../xmppPubSubApi"
import { captureError } from "@/ErrorHandlers"
import { Tacho } from "@/reducers/tachosSlice"
import { readingFromElement } from "@/lib/tachoReader"

const NODE_BATCH = "urn:slickshift:tacho-batch:0"

export const retrieveAllTachos = async (xmpp: Client): Promise<Tacho[]> => {
  const tachos = await getAllItems({
    xmpp,
    jid: wizardJid(xmpp.jid!),
    node: NODE_BATCH,
    timeout: 4000,
  })

  const items = tachos
    .getChild("pubsub")
    ?.getChild("items")
    ?.getChildren("item") as [Element]

  return items.flatMap((item) => {
    const tachoBatch = item.getChild("tacho-batch") as Element
    return tachoBatch.getChildren("item").map((tachoBatchItem) => {
      const tacho = tachoBatchItem.getChild("tacho") as Element

      return buildTachoFromXml(tachoBatchItem.getAttr("id"), tacho)
    })
  })
}

const buildTachoFromXml = (itemId: string, item: Element): Tacho => {
  const deviceId = item.getAttr("device_id")
  const remainingSecondsToday = parseInt(
    item.getAttr("remaining_seconds_today") ?? "0",
    10,
  )
  const usedSecondsToday = parseInt(
    item.getAttr("used_seconds_today") ?? "0",
    10,
  )

  const remainingMiddayBreakSeconds = item.getAttr(
    "remaining_midday_break_seconds",
  )
    ? parseInt(item.getAttr("remaining_midday_break_seconds")!, 10)
    : null
  const remainingBeforeDayStartBreakSeconds = item.getAttr(
    "remaining_before_day_start_break_seconds",
  )
    ? parseInt(item.getAttr("remaining_before_day_start_break_seconds")!, 10)
    : null
  const remainingAfterDayEndBreakSeconds = item.getAttr(
    "remaining_after_day_end_break_seconds",
  )
    ? parseInt(item.getAttr("remaining_after_day_end_break_seconds")!, 10)
    : null
  const remainingSecondsThisWeek = item.getAttr("remaining_seconds_this_week")
    ? parseInt(item.getAttr("remaining_seconds_this_week")!, 10)
    : null
  const remainingSecondsOfUninterruptedDriving = item.getAttr(
    "remaining_seconds_of_uninterrupted_driving",
  )
    ? parseInt(item.getAttr("remaining_seconds_of_uninterrupted_driving")!, 10)
    : null
  const remainingWeeklyRestSeconds = item.getAttr(
    "remaining_weekly_rest_seconds",
  )
    ? parseInt(item.getAttr("remaining_weekly_rest_seconds")!, 10)
    : null
  const remainingShortenedDailyRests = item.getAttr(
    "remaining_shortened_daily_rests",
  )
    ? parseInt(item.getAttr("remaining_shortened_daily_rests")!, 10)
    : null
  const remainingSecondsTwoWeeks = item.getAttr("remaining_seconds_two_weeks")
    ? parseInt(item.getAttr("remaining_seconds_two_weeks")!, 10)
    : null

  const dayEndTimestamp = item.getAttr("day_end_timestamp")
    ? new Date(item.getAttr("day_end_timestamp")!).toLocaleString("pl-PL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null

  const remainingDailyExtensions = item.getAttr("remaining_daily_extensions")
    ? parseInt(item.getAttr("remaining_daily_extensions")!, 10)
    : null

  const currentActivity = item.getAttr("current_activity")
  const timestamp = item.getAttr("timestamp")
  const doubleCrew = item.getAttr("double_crew") === "true"

  return {
    jid: itemId,
    deviceId,
    remainingSecondsToday,
    usedSecondsToday,
    remainingMiddayBreakSeconds,
    remainingBeforeDayStartBreakSeconds,
    remainingAfterDayEndBreakSeconds,
    remainingSecondsThisWeek,
    remainingSecondsOfUninterruptedDriving,
    remainingWeeklyRestSeconds,
    remainingShortenedDailyRests,
    remainingSecondsTwoWeeks,
    currentActivity,
    timestamp,
    dayEndTimestamp,
    remainingDailyExtensions,
    doubleCrew,
    ...readingFromElement(item),
  }
}

export const subscribeToTachos = async (client: Client) => {
  try {
    await XmppPubSubApi.subscribe(
      client,
      wizardJid(client.jid!),
      "urn:slickshift:tacho:0",
    )
  } catch (error: any) {
    if (error.condition !== "item-not-found") {
      captureError(error, {
        origin: "XMPPOnOnline",
        extra: { message: "subscribeToTachos" },
      })
    }
  }
}
